// SPDX-License-Identifier: AGPL-3.0-or-later

import bronzeIcon from '@app/media/images/badges/raven-tenure/bronze.svg';
import bronzeArtwork from '@app/media/images/badges/raven-tenure/bronze.png';
import silverIcon from '@app/media/images/badges/raven-tenure/silver.svg';
import silverArtwork from '@app/media/images/badges/raven-tenure/silver.png';
import goldIcon from '@app/media/images/badges/raven-tenure/gold.svg';
import goldArtwork from '@app/media/images/badges/raven-tenure/gold.png';
import platinumIcon from '@app/media/images/badges/raven-tenure/platinum.svg';
import platinumArtwork from '@app/media/images/badges/raven-tenure/platinum.png';
import diamondIcon from '@app/media/images/badges/raven-tenure/diamond.svg';
import diamondArtwork from '@app/media/images/badges/raven-tenure/diamond.png';
import emeraldIcon from '@app/media/images/badges/raven-tenure/emerald.svg';
import emeraldArtwork from '@app/media/images/badges/raven-tenure/emerald.png';
import rubyIcon from '@app/media/images/badges/raven-tenure/ruby.svg';
import rubyArtwork from '@app/media/images/badges/raven-tenure/ruby.png';
import opalIcon from '@app/media/images/badges/raven-tenure/opal.svg';
import opalArtwork from '@app/media/images/badges/raven-tenure/opal.png';
import {UserFlags} from '@fluxer/constants/src/UserConstants';

export type RavenTenureBadgeKey = 'bronze' | 'silver' | 'gold' | 'platinum' | 'diamond' | 'emerald' | 'ruby' | 'opal';

export interface RavenTenureBadge {
	key: RavenTenureBadgeKey;
	name: string;
	/** Raven's completed calendar-month threshold for the selected badge. */
	months: number;
	icon: string;
	artwork: string;
	nextMonths: number | null;
	/** Fraction from this tier's anniversary to the next, clamped to 0..1. */
	progress: number;
}

// These thresholds are Raven policy. The supplied artwork does not define billing or access rights.
const TIERS = [
	{key: 'bronze', name: 'Bronze', months: 1, icon: bronzeIcon, artwork: bronzeArtwork, flag: UserFlags.RAVEN_TENURE_BRONZE},
	{key: 'silver', name: 'Silver', months: 3, icon: silverIcon, artwork: silverArtwork, flag: UserFlags.RAVEN_TENURE_SILVER},
	{key: 'gold', name: 'Gold', months: 6, icon: goldIcon, artwork: goldArtwork, flag: UserFlags.RAVEN_TENURE_GOLD},
	{key: 'platinum', name: 'Platinum', months: 12, icon: platinumIcon, artwork: platinumArtwork, flag: UserFlags.RAVEN_TENURE_PLATINUM},
	{key: 'diamond', name: 'Diamond', months: 24, icon: diamondIcon, artwork: diamondArtwork, flag: UserFlags.RAVEN_TENURE_DIAMOND},
	{key: 'emerald', name: 'Emerald', months: 36, icon: emeraldIcon, artwork: emeraldArtwork, flag: UserFlags.RAVEN_TENURE_EMERALD},
	{key: 'ruby', name: 'Ruby', months: 60, icon: rubyIcon, artwork: rubyArtwork, flag: UserFlags.RAVEN_TENURE_RUBY},
	{key: 'opal', name: 'Opal', months: 72, icon: opalIcon, artwork: opalArtwork, flag: UserFlags.RAVEN_TENURE_OPAL},
] as const;

/** Artwork catalog for previews; a catalog entry does not mean the badge was awarded. */
export const RAVEN_TENURE_BADGES: ReadonlyArray<Omit<RavenTenureBadge, 'nextMonths' | 'progress'>> = TIERS.map(
	({key, name, months, icon, artwork}) => ({key, name, months, icon, artwork}),
);

function anniversary(since: Date, months: number): number {
	const date = new Date(since.getTime());
	const targetMonth = since.getUTCMonth() + months;
	date.setUTCDate(1);
	date.setUTCMonth(targetMonth);
	const endOfMonth = new Date(date.getTime());
	endOfMonth.setUTCMonth(endOfMonth.getUTCMonth() + 1, 0);
	date.setUTCDate(Math.min(since.getUTCDate(), endOfMonth.getUTCDate()));
	return date.getTime();
}

/** Selects a cosmetic badge. Callers must check real premium entitlement separately. */
export function getRavenTenureBadge(
	premiumSince: string | null | undefined,
	flags: bigint = 0n,
	now: Date = new Date(),
): RavenTenureBadge | null {
	const nowTimestamp = now.getTime();
	const since = premiumSince ? new Date(premiumSince) : null;
	const validSince = since && Number.isFinite(since.getTime()) && Number.isFinite(nowTimestamp) && since.getTime() <= nowTimestamp
		? since
		: null;
	// Pick one highest manual tier if historical/bulk edits left multiple cosmetic bits.
	let index = TIERS.findLastIndex((tier) => (flags & tier.flag) !== 0n);
	if (index < 0 && validSince) {
		index = TIERS.findLastIndex((tier) => nowTimestamp >= anniversary(validSince, tier.months));
	}
	if (index < 0) return null;
	const tier = TIERS[index];
	const nextMonths = TIERS[index + 1]?.months ?? null;
	let progress = 0;
	if (validSince && nowTimestamp >= anniversary(validSince, tier.months)) {
		if (nextMonths === null) {
			progress = 1;
		} else {
			const currentAnniversary = anniversary(validSince, tier.months);
			const nextAnniversary = anniversary(validSince, nextMonths);
			progress = Math.max(0, Math.min(1, (nowTimestamp - currentAnniversary) / (nextAnniversary - currentAnniversary)));
		}
	}
	return {key: tier.key, name: tier.name, months: tier.months, icon: tier.icon, artwork: tier.artwork, nextMonths, progress};
}
